import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { getInvitationByToken, acceptInvitation, addParticipant } from '../../services/meetingService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const JoinMeeting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [participantName, setParticipantName] = useState('');
  const [location, setLocation] = useState('');
  const [transportMethod, setTransportMethod] = useState('');

  const transportOptions = [
    { value: 'driving', label: 'Driving' },
    { value: 'walking', label: 'Walking' },
    { value: 'public-transit', label: 'Public Transit' },
    { value: 'bike', label: 'Bike' }
  ];

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const invitationData = await getInvitationByToken(token);

      if (!invitationData) {
        setError('Invitation not found or expired');
        return;
      }

      if (invitationData?.status === 'accepted') {
        setError('This invitation has already been accepted');
        return;
      }

      if (invitationData?.status === 'declined') {
        setError('This invitation has been declined');
        return;
      }

      setInvitation(invitationData);
      setParticipantName(invitationData?.inviteeName || '');
    } catch (err) {
      setError(err?.message || 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinMeeting = async () => {
    if (!participantName?.trim()) {
      toast?.error('Please enter your name');
      return;
    }

    if (!location?.trim()) {
      toast?.error('Please enter your location');
      return;
    }

    if (!transportMethod) {
      toast?.error('Please select transportation method');
      return;
    }

    try {
      setSubmitting(true);

      // Calculate mock travel time (in real app, use Google Maps API)
      const travelTime = Math.floor(Math.random() * 30) + 10; // 10-40 minutes
      const arrivalTime = new Date(Date.now() + travelTime * 60000)?.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });

      // Add participant to meeting
      await addParticipant({
        meetingId: invitation?.meetings?.id,
        participantName: participantName?.trim(),
        participantLocation: location?.trim(),
        transportMethod,
        travelTime,
        arrivalTime
      });

      // Accept invitation
      await acceptInvitation(invitation?.id);

      toast?.success('Successfully joined the meeting!');
      setTimeout(() => {
        navigate(`/meeting-confirmation?meetingId=${invitation?.meetings?.id}`);
      }, 1500);
    } catch (error) {
      console.error('Join meeting error:', error);
      toast?.error(error?.message || 'Failed to join meeting');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position?.coords;
          
          // Try to reverse geocode the coordinates to get actual address
          try {
            const apiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
            
            if (apiKey && apiKey !== 'your-google-maps-api-key-here') {
              // Use Google Geocoding API to convert coordinates to address
              const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
              );
              const data = await response?.json();
              
              if (data?.status === 'OK' && data?.results?.[0]) {
                const address = data?.results?.[0]?.formatted_address;
                setLocation(address);
                toast?.success('Location detected');
                return;
              }
            }
            
            // Fallback: Use coordinates as location string
            setLocation(`${latitude?.toFixed(6)}, ${longitude?.toFixed(6)}`);
            toast?.success('Location detected');
          } catch (error) {
            console.error('Reverse geocoding error:', error);
            // Fallback: Use coordinates as location string
            setLocation(`${latitude?.toFixed(6)}, ${longitude?.toFixed(6)}`);
            toast?.success('Location detected');
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast?.error('Could not detect location. Please check location permissions.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      toast?.error('Geolocation is not supported by your browser');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-destructive" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
              Invalid Invitation
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Join Meeting - Mosey</title>
      </Helmet>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-card border border-border rounded-xl p-6 md:p-8 mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name="Mail" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
                {invitation?.userProfiles?.fullName} is inviting you to meetup with Mosey
              </h1>
              <p className="text-muted-foreground">
                You have been invited to join a meeting. Enter your location and transportation method to participate.
              </p>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <h3 className="font-heading font-semibold text-foreground mb-3">Meeting Details</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span className="text-sm text-foreground">{invitation?.meetings?.locationName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} className="text-primary" />
                <span className="text-sm text-foreground">{invitation?.meetings?.meetingDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-sm text-foreground">{invitation?.meetings?.meetingTime}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 md:p-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
            Your Information
          </h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Your Name *
              </label>
              <Input
                type="text"
                value={participantName}
                onChange={(e) => setParticipantName(e?.target?.value)}
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Your Location *
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e?.target?.value)}
                  placeholder="Enter your starting location"
                  className="flex-1"
                />
                <Button
                  onClick={handleUseCurrentLocation}
                  variant="outline"
                  className="flex-shrink-0"
                >
                  <Icon name="Navigation" size={20} />
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Transportation Method *
              </label>
              <Select
                value={transportMethod}
                onChange={(e) => setTransportMethod(e?.target?.value)}
                options={transportOptions}
                placeholder="Select transportation"
              />
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground mb-1 font-medium">
                    Fair Meeting Coordination
                  </p>
                  <p className="text-xs text-muted-foreground">
                    We will calculate the optimal meeting point based on everyone's location and travel time to ensure fairness.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleJoinMeeting}
              className="w-full"
              disabled={submitting}
            >
              {submitting ? 'Joining...' : 'Join Meeting'}
            </Button>
          </div>
        </div>
      </main>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default JoinMeeting;